import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";

// Minimal React wrapper around ECharts 5. Keeps a single instance per mount,
// resizes on window resize AND container size change, and re-applies options on change.
const EChart = ({ option, style, height = 260, theme, onEvents }) => {
  const domRef = useRef(null);
  const instRef = useRef(null);

  useEffect(() => {
    if (!domRef.current) return;
    instRef.current = echarts.init(domRef.current, theme || undefined, { renderer: "canvas" });

    const onResize = () => instRef.current && instRef.current.resize();
    window.addEventListener("resize", onResize);

    // ResizeObserver fires whenever the container's dimensions change, which
    // catches responsive layout reflows that don't change window.innerWidth
    // (e.g., panel collapse, sidebar toggle, flex wrap).
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        if (instRef.current) instRef.current.resize();
      });
      ro.observe(domRef.current);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
      if (instRef.current) {
        instRef.current.dispose();
        instRef.current = null;
      }
    };
  }, [theme]);

  useEffect(() => {
    if (!instRef.current || !option) return;
    instRef.current.setOption(option, { notMerge: true });
    if (onEvents) {
      Object.entries(onEvents).forEach(([name, handler]) => {
        instRef.current.off(name);
        instRef.current.on(name, handler);
      });
    }
  }, [option, onEvents]);

  return <div ref={domRef} style={{ width: "100%", height, ...style }} />;
};


// Palette for both light and dark modes. Based on the Tableau 10 qualitative
// set with two tweaks (amber + slate) so white labels stay readable on every
// treemap/bar cell. Also exports semantic maps for risk and op-class so
// encodings stay consistent across dashboards.
export const TABLEAU_10 = [
  "#4e79a7", // blue
  "#f28e2b", // orange
  "#e15759", // red
  "#76b7b2", // teal
  "#59a14f", // green
  "#d97706", // amber (darker than Tableau's yellow for label contrast)
  "#b07aa1", // violet
  "#ff9da7", // pink
  "#9c755f", // brown
  "#64748b", // slate (darker than Tableau's gray for contrast)
];

export const RISK_COLORS = {
  low:    "#2ca02c",
  medium: "#f59e0b",
  high:   "#d62728",
  "n/a":  "#94a3b8",
};

export const OP_CLASS_COLORS = {
  read:        "#4e79a7",
  write:       "#59a14f",
  share:       "#f28e2b",
  structure:   "#b07aa1",
  destructive: "#e15759",
  admin:       "#9c755f",
  comment:     "#76b7b2",
  tag:         "#d97706",
};

export const echartsPalette = (mode = "light") => {
  const isDark = mode === "dark";
  return {
    colors: TABLEAU_10,
    brand:     "#8237bb",
    brandSoft: "#b078ff",
    brandDeep: "#5a1f87",
    risk: RISK_COLORS,
    opClass: OP_CLASS_COLORS,
    text:          isDark ? "#d1d5db" : "#374151",
    textSoft:      isDark ? "#9aa0ae" : "#6b7280",
    grid:          isDark ? "#2b2433" : "#e5e7eb",
    cardBg:        isDark ? "#1c1324" : "#ffffff",
    tooltipBg:     isDark ? "#14101a" : "#ffffff",
    tooltipBorder: isDark ? "#2b2433" : "#d1d5db",
    danger:  "#e15759",
    warning: "#f59e0b",
    success: "#2ca02c",
  };
};

export default EChart;
